import { FieldOption } from 'views/loans/Pipeline/FilterDialog';
import { AppointmentAIType } from 'types/contact';

export interface FilterOptionCategoryGroup {
  title: string;
  id: string | number;
  fields: FieldOption[];
}

export interface AppointmentModeOption {
  id: number;
  label: string;
  value: AppointmentAIType;
}

export const APPOINTMENT_MODE_OPTIONS: AppointmentModeOption[] = [
  {
    id: 1,
    label: 'Off',
    value: 'disabled'
  },
  {
    id: 2,
    label: 'Book an Appointment',
    value: 'schedule_only'
  },
  {
    id: 3,
    label: 'Send Application Link',
    value: 'application_mode'
  },
  {
    id: 4,
    label: 'Book an Appointment Or Send Application',
    value: 'booking_and_application_mode'
  },
  {
    id: 5,
    label: 'Answer Questions',
    value: 'answer_questions'
  }
];

/**
 * The two "pipeline stages" smart-filter fields: `pipelineStages` targets the
 * borrower loan pipelines, `partnerPipelineStages` the (separate) partner
 * pipeline system. They share the `{ pipelineId, stageIds }` value shape, so
 * every value-keyed branch in the filter UI must accept both.
 */
export const PIPELINE_STAGES_FIELDS = ['pipelineStages', 'partnerPipelineStages'];

export const isPipelineStages = (field: string): boolean => PIPELINE_STAGES_FIELDS.includes(field);

export const fieldsOptions: FieldOption[] = [
  {
    id: 1,
    value: 'firstName',
    label: 'First Name',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' }
    ]
  },
  {
    id: 2,
    value: 'lastName',
    label: 'Last Name',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' }
    ]
  },
  {
    id: 3,
    value: 'email',
    label: 'Email',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' },
      { id: 7, value: 'IS_INVALID', label: 'Invalid Email', fieldType: 'none' }
    ]
  },
  {
    id: 4,
    value: 'phone',
    label: 'Phone',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'phone' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'phone' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'phone' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'phone' },
      { id: 7, value: 'IS_INVALID', label: 'Invalid Phone', fieldType: 'none' },
      { id: 8, value: 'IS_LANDLINE', label: 'Landline', fieldType: 'none' }
    ]
  },
  {
    id: 5,
    value: 'newTags',
    label: 'Tag',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'tags' },
      { id: 2, value: 'IS', label: 'Is', fieldType: 'tag' },
      { id: 3, value: 'IS_NOT', label: 'Is Not', fieldType: 'tag' }
    ]
  },
  {
    id: 6,
    value: 'zipCode',
    label: 'Zip Code',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'zipCode' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'zipCode' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'zipCode' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'zipCode' }
    ]
  },
  {
    id: 7,
    value: 'creditScore',
    label: 'Credit Score',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'number' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'number' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 8,
    value: 'dob',
    label: 'Date of Birth',
    operators: [
      { id: 1, value: 'TODAY', label: 'Today', fieldType: 'fixedDateRange' },
      { id: 2, value: 'THIS_WEEK', label: 'This Week', fieldType: 'fixedDateRange' },
      { id: 3, value: 'THIS_MONTH', label: 'This Month', fieldType: 'fixedDateRange' },
      { id: 4, value: 'IN_MONTH', label: 'In Month', fieldType: 'months' },
      { id: 5, value: 'RANGE', label: 'Date Range', fieldType: 'dateRange' },
      { id: 8, value: 'BIRTHDAY_RANGE', label: 'Birthday Range', fieldType: 'birthdayRange' },
      { id: 6, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 7, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 9,
    value: 'createdAt',
    label: 'Borrower Creation',
    operators: [
      { id: 1, value: 'TODAY', label: 'Today', fieldType: 'fixedDateRange' },
      { id: 2, value: 'THIS_WEEK', label: 'This Week', fieldType: 'fixedDateRange' },
      { id: 3, value: 'THIS_MONTH', label: 'This Month', fieldType: 'fixedDateRange' },
      { id: 4, value: 'RANGE', label: 'Date Range', fieldType: 'dateRange' }
    ]
  },
  {
    id: 10,
    value: 'dnd',
    label: 'DND',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'dnd' }]
  },
  {
    id: 11,
    value: 'newSource',
    label: 'Borrower Source',
    operators: [
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'sources' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'sources' }
    ]
  },
  {
    id: 12,
    value: 'newStatus',
    label: 'Borrower Status',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'status' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'status' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'status' }
    ]
  },
  {
    id: 13,
    value: 'owners',
    label: 'Owners',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'owners' }]
  },
  {
    id: 14,
    value: 'followers',
    label: 'Followers',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'followers' }]
  },
  {
    id: 15,
    value: 'engagementLevel',
    label: 'Engagement Level',
    operators: [
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'engagementLevel' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'engagementLevel' }
    ]
  },
  {
    id: 16,
    value: 'activeCampaign',
    label: 'Active Campaign',
    operators: [
      { id: 1, value: 'IS', label: 'Is', fieldType: 'activeCampaign' },
      { id: 2, value: 'IS_NOT', label: 'Is Not', fieldType: 'activeCampaign' },
      { id: 3, value: 'ANY_OF', label: 'Any Of', fieldType: 'activeCampaigns' }
    ]
  },
  {
    id: 17,
    value: 'aiMode',
    label: 'Marketing Mode',
    operators: [
      { id: 1, value: 'IS', label: 'Is', fieldType: 'aiMode' },
      { id: 2, value: 'IS_NOT', label: 'Is Not', fieldType: 'aiMode' }
    ]
  },
  {
    id: 18,
    value: 'aiAppointmentMode',
    label: 'Appointment Mode',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'aiAppointmentMode' }]
  },
  {
    id: 19,
    value: 'rateWatchStatus',
    label: 'Rate Watch Status',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'rateWatchStatus' }]
  },
  {
    id: 20,
    value: 'closedDate',
    label: 'Loan Closed Date',
    operators: [
      { id: 1, value: 'TODAY', label: 'Today', fieldType: 'fixedDateRange' },
      { id: 2, value: 'THIS_WEEK', label: 'This Week', fieldType: 'fixedDateRange' },
      { id: 3, value: 'THIS_MONTH', label: 'This Month', fieldType: 'fixedDateRange' },
      { id: 4, value: 'IN_MONTH', label: 'In Month', fieldType: 'months' },
      { id: 5, value: 'RANGE', label: 'Date Range', fieldType: 'dateRange' }
    ]
  },
  {
    id: 21,
    value: 'loanType',
    label: 'Loan Type',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'loanType' }]
  },
  {
    id: 22,
    value: 'aiIntent',
    label: 'Loan Purpose',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'loanPurpose' }]
  },
  {
    id: 23,
    value: 'state',
    label: 'Borrower State',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'state' }]
  },
  {
    id: 24,
    value: 'loanTerm',
    label: 'Loan Term',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'number' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'number' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'number' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'number' }
    ]
  },
  {
    id: 25,
    value: 'loanLender',
    label: 'Loan Lender',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'loanLender' },
      { id: 2, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 3, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 26,
    value: 'pipeline',
    label: 'Pipeline',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'pipeline' },
      { id: 2, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 3, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 27,
    value: 'pipelineStages',
    label: 'Pipeline Stages',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'pipelineStages' }]
  },
  {
    id: 28,
    value: 'loanAmount',
    label: 'Loan Amount',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'money' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'money' },
      { id: 3, value: 'RANGE', label: 'Range', fieldType: 'moneyRange' }
    ]
  },
  {
    id: 28,
    value: 'loanAmount',
    label: 'Loan Amount',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'money' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'money' },
      { id: 3, value: 'RANGE', label: 'Range', fieldType: 'moneyRange' }
    ]
  },
  {
    id: 29,
    value: 'loanInterestRate',
    label: 'Loan Interest Rate',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'percentage-float' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'percentage-float' },
      { id: 3, value: 'RANGE', label: 'Range', fieldType: 'percentageRange' }
    ]
  },
  {
    id: 30,
    value: 'propertyType',
    label: 'Property Type',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'propertyType' }]
  },
  {
    id: 31,
    value: 'mortgageType',
    label: 'Mortgage Type',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'mortgageType' }]
  },
  {
    id: 32,
    value: 'propertyPulse',
    label: 'Property Pulse',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'propertyPulse' }]
  },
  {
    id: 33,
    value: 'address',
    label: 'Borrower Address',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 34,
    value: 'opportunityType',
    label: 'Opportunity',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'opportunityTypeBorrower' },
      { id: 2, value: 'IS', label: 'Is', fieldType: 'opportunityTypeBorrower' }
    ]
  },
  {
    id: 35,
    value: 'leadScoreTier',
    label: 'Lead Score Tier',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'leadScoreTier' },
      { id: 2, value: 'IS_NOT', label: 'Is not', fieldType: 'leadScoreTier' }
    ]
  },
  {
    id: 36,
    value: 'leadScoreState',
    label: 'Lead Score Status',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'leadScoreState' },
      { id: 2, value: 'IS_NOT', label: 'Is not', fieldType: 'leadScoreState' }
    ]
  }
];

export const fieldsGroupedOptions: FilterOptionCategoryGroup[] = [
  {
    title: 'Personal Information',
    id: 'personal_information',
    fields: [
      // By value, not position. First/Last Name were dropped from this group by
      // accident in d227d504 (May 2025) and never restored; the partner group
      // has always offered them in this order.
      ...fieldsOptions.filter((f) => f.value === 'firstName'),
      ...fieldsOptions.filter((f) => f.value === 'lastName'),
      fieldsOptions[3],
      fieldsOptions[2],
      fieldsOptions[7],
      fieldsOptions[5],
      fieldsOptions[22],
      fieldsOptions[33]
    ]
  },
  {
    title: 'Borrower Status & Qualification',
    id: 'borrower_status_qualification',
    fields: [
      fieldsOptions[6],
      fieldsOptions[11],
      // Looked up by value, not position: fieldsOptions carries a duplicate id,
      // so `id` and array index stop lining up partway through.
      ...fieldsOptions.filter((f) => f.value === 'leadScoreTier' || f.value === 'leadScoreState')
    ]
  },
  {
    title: 'Marketing & Communication',
    id: 'marketing_communication',
    fields: [fieldsOptions[15], fieldsOptions[16], fieldsOptions[17], fieldsOptions[9], fieldsOptions[32]]
  },
  {
    title: 'Source & Tracking',
    id: 'source_tracking',
    fields: [fieldsOptions[10], fieldsOptions[4], fieldsOptions[8], fieldsOptions[12], fieldsOptions[13]]
  },
  {
    title: 'Loan & Rate Tracking',
    id: 'loan_rate_tracking',
    fields: [
      fieldsOptions[18],
      fieldsOptions[20],
      fieldsOptions[21],
      fieldsOptions[19],
      fieldsOptions[23],
      fieldsOptions[24],
      fieldsOptions[25],
      fieldsOptions[26],
      fieldsOptions[27],
      fieldsOptions[28],
      fieldsOptions[29],
      fieldsOptions[30],
      fieldsOptions[31]
    ]
  },
  {
    title: 'Opportunities',
    id: 'opportunities',
    fields: [fieldsOptions[34]]
  }
];

export const fieldsPartnerOptions: FieldOption[] = [
  {
    id: 1,
    value: 'firstName',
    label: 'First Name',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' }
    ]
  },
  {
    id: 2,
    value: 'lastName',
    label: 'Last Name',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' }
    ]
  },
  {
    id: 3,
    value: 'email',
    label: 'Email',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' },
      { id: 7, value: 'IS_INVALID', label: 'Invalid Email', fieldType: 'none' }
    ]
  },
  {
    id: 4,
    value: 'phone',
    label: 'Phone',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'phone' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'phone' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'phone' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'phone' },
      { id: 7, value: 'IS_INVALID', label: 'Invalid Phone', fieldType: 'none' },
      { id: 8, value: 'IS_LANDLINE', label: 'Landline', fieldType: 'none' }
    ]
  },
  {
    id: 5,
    value: 'partnerTags',
    label: 'Partner Tag',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'partnerTags' },
      { id: 2, value: 'IS', label: 'Is', fieldType: 'partnerTag' },
      { id: 3, value: 'IS_NOT', label: 'Is Not', fieldType: 'partnerTag' }
    ]
  },
  {
    id: 6,
    value: 'zipCode',
    label: 'Zip Code',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'zipCode' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'zipCode' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'zipCode' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'zipCode' }
    ]
  },
  {
    id: 7,
    value: 'creditScore',
    label: 'Credit Score',
    operators: [
      { id: 1, value: 'GREATER_THAN', label: 'Greater Than', fieldType: 'number' },
      { id: 2, value: 'LESSER_THAN', label: 'Lesser Than', fieldType: 'number' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 8,
    value: 'dob',
    label: 'Date of Birth',
    operators: [
      { id: 1, value: 'TODAY', label: 'Today', fieldType: 'fixedDateRange' },
      { id: 2, value: 'THIS_WEEK', label: 'This Week', fieldType: 'fixedDateRange' },
      { id: 3, value: 'THIS_MONTH', label: 'This Month', fieldType: 'fixedDateRange' },
      { id: 4, value: 'IN_MONTH', label: 'In Month', fieldType: 'months' },
      { id: 5, value: 'RANGE', label: 'Date Range', fieldType: 'dateRange' },
      { id: 8, value: 'BIRTHDAY_RANGE', label: 'Birthday Range', fieldType: 'birthdayRange' },
      { id: 6, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 7, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 9,
    value: 'createdAt',
    label: 'Partner Creation',
    operators: [
      { id: 1, value: 'TODAY', label: 'Today', fieldType: 'fixedDateRange' },
      { id: 2, value: 'THIS_WEEK', label: 'This Week', fieldType: 'fixedDateRange' },
      { id: 3, value: 'THIS_MONTH', label: 'This Month', fieldType: 'fixedDateRange' },
      { id: 4, value: 'RANGE', label: 'Date Range', fieldType: 'dateRange' }
    ]
  },
  {
    id: 10,
    value: 'dnd',
    label: 'DND',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'dnd' }]
  },
  {
    id: 11,
    value: 'partnerStatus',
    label: 'Partner Status',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'partnerStatus' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'partnerStatus' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'partnerStatus' }
    ]
  },
  {
    id: 12,
    value: 'owners',
    label: 'Owners',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'owners' }]
  },
  {
    id: 13,
    value: 'followers',
    label: 'Followers',
    operators: [{ id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'followers' }]
  },
  {
    id: 14,
    value: 'engagementLevel',
    label: 'Engagement Level',
    operators: [
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'engagementLevel' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'engagementLevel' }
    ]
  },
  {
    id: 15,
    value: 'activeCampaign',
    label: 'Active Campaign',
    operators: [
      { id: 1, value: 'IS', label: 'Is', fieldType: 'activeCampaign' },
      { id: 2, value: 'IS_NOT', label: 'Is Not', fieldType: 'activeCampaign' },
      { id: 3, value: 'ANY_OF', label: 'Any Of', fieldType: 'activeCampaigns' }
    ]
  },
  {
    id: 16,
    value: 'aiMode',
    label: 'Marketing Mode',
    operators: [
      { id: 1, value: 'IS', label: 'Is', fieldType: 'aiMode' },
      { id: 2, value: 'IS_NOT', label: 'Is Not', fieldType: 'aiMode' }
    ]
  },
  {
    id: 17,
    value: 'partnerIndustry',
    label: 'Industry',
    operators: [
      { id: 1, value: 'IS', label: 'Is', fieldType: 'partnerIndustry' },
      { id: 2, value: 'IS_NOT', label: 'Is Not', fieldType: 'partnerIndustry' }
    ]
  },
  {
    id: 18,
    value: 'partnerSource',
    label: 'Partner Source',
    operators: [
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'partnerSources' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'partnerSources' }
    ]
  },
  // Partner pipelines are a separate system from the borrower (loan) pipelines
  // filtered by `pipeline`/`pipelineStages`, so the field values differ — the
  // backend resolves them against `contact.partnerPipelineStageId`. The
  // `fieldType`s are deliberately shared with the borrower entries: FilterGroup
  // renders one pipeline picker and swaps its data source on `isPartners`.
  {
    id: 19,
    value: 'partnerPipeline',
    label: 'Pipeline',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any Of', fieldType: 'pipeline' },
      { id: 2, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 3, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' }
    ]
  },
  {
    id: 20,
    value: 'partnerPipelineStages',
    label: 'Pipeline Stages',
    operators: [{ id: 1, value: 'IS', label: 'Is', fieldType: 'pipelineStages' }]
  },
  {
    id: 35,
    value: 'leadScoreTier',
    label: 'Engagement Score Tier',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'leadScoreTier' },
      { id: 2, value: 'IS_NOT', label: 'Is not', fieldType: 'leadScoreTier' }
    ]
  },
  {
    id: 36,
    value: 'leadScoreState',
    label: 'Engagement Score Status',
    operators: [
      { id: 1, value: 'ANY_OF', label: 'Any of', fieldType: 'leadScoreState' },
      { id: 2, value: 'IS_NOT', label: 'Is not', fieldType: 'leadScoreState' }
    ]
  },
  // Free text, not a picker: `partnerCompany` is a plain varchar typed into the
  // partner form, and production holds 84,487 distinct values across all
  // companies (663 for the customer who asked for this filter) — far past what
  // a dropdown can carry. Same operator set as First Name / Last Name.
  // "Is Empty" is exact here despite the column storing `''` for most unset
  // partners: the backend gives partnerCompany a NULLIF(TRIM(...)) test
  // (smart-list.utils.ts, EMPTY_STRING_AS_NULL_FIELDS), so this field needs
  // the backend change deployed first.
  {
    id: 37,
    value: 'partnerCompany',
    label: 'Company',
    operators: [
      { id: 1, value: 'CONTAINS', label: 'Contains', fieldType: 'text' },
      { id: 2, value: 'NOT_CONTAINS', label: 'Not Contains', fieldType: 'text' },
      { id: 3, value: 'IS_EMPTY', label: 'Is Empty', fieldType: 'none' },
      { id: 4, value: 'IS_NOT_EMPTY', label: 'Is Not Empty', fieldType: 'none' },
      { id: 5, value: 'IS', label: 'Is', fieldType: 'text' },
      { id: 6, value: 'IS_NOT', label: 'Is Not', fieldType: 'text' }
    ]
  }
];

export const fieldsGroupedPartner: FilterOptionCategoryGroup[] = [
  {
    title: 'Personal Information',
    id: 'personal_information',
    fields: [
      fieldsPartnerOptions[0],
      fieldsPartnerOptions[1],
      fieldsPartnerOptions[16],
      // By value, not position — the array is index-referenced throughout this
      // object, so appending `partnerCompany` to its end (the only safe place
      // to add) would otherwise put it in whichever group sits at that offset.
      // Sits next to Industry because the two are asked for together.
      ...fieldsPartnerOptions.filter((f) => f.value === 'partnerCompany'),
      fieldsPartnerOptions[2],
      fieldsPartnerOptions[3],
      fieldsPartnerOptions[7],
      fieldsPartnerOptions[5]
    ]
  },
  {
    title: 'Marketing & Status',
    id: 'marketing_status',
    fields: [
      // By value, not position: fieldsPartnerOptions is index-referenced here
      // and appending by index puts a new field in whichever group happens to
      // sit at that offset.
      ...fieldsPartnerOptions.filter((f) => f.value === 'leadScoreTier' || f.value === 'leadScoreState'),
      fieldsPartnerOptions[4],
      fieldsPartnerOptions[8],
      fieldsPartnerOptions[10],
      fieldsPartnerOptions[17],
      fieldsPartnerOptions[11],
      fieldsPartnerOptions[12],
      fieldsPartnerOptions[14],
      fieldsPartnerOptions[9]
    ]
  },
  {
    title: 'Pipeline',
    id: 'pipeline',
    // Indexes, not ids — `fieldsPartnerOptions` is referenced positionally, so
    // new fields must be appended to the end of that array.
    fields: [fieldsPartnerOptions[18], fieldsPartnerOptions[19]]
  }
];
